import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Slide,
  Tooltip,
  Box,
  Typography,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  Chip,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import moment from 'moment';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { jsPDF } from 'jspdf';

import { getMedicines } from '../../apis/medicineSlice';
import { getPharmacyShops } from '../../apis/pharmacyShopSlice';
import { getPurchaseOrders, createPurchaseOrder, importPurchaseOrder, cancelPurchaseOrder, getPurchaseOrderById, receivePurchaseOrder } from '../../apis/purchaseOrderSlice';

import SearchClinic from '../../components/Autocomplete/SearchClinic';
import SearchPharmacyShop from '../../components/Autocomplete/SearchPharmacyShop';
import SearchMedicine from '../../components/Autocomplete/SearchMedicine';
import { toast } from 'react-toastify';
import autoTable from 'jspdf-autotable';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CommonAgGrid from '../../components/CommonAgGrid/CommonAgGrid';
import DoctorStyle from '../doctors/Doctors.module.scss';


export default function ImportMedicine() {
  const dispatch = useDispatch();
  const { orders, totalCount, loading: ordersLoading } = useSelector(
    (state) => state.purchaseOrderData || { orders: [], totalCount: 0, loading: false },
  );

  const [medicines, setMedicines] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);

  // PO dialog state
  const [poOpen, setPoOpen] = useState(false);
  const [poClinic, setPoClinic] = useState('');
  const [poShop, setPoShop] = useState('');
  const [items, setItems] = useState([{ medicineId: '', qty: 1 }]);
  const [loading, setLoading] = useState(false);
  // report dialog state
  const [reportOpen, setReportOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [receiveRows, setReceiveRows] = useState([]);

  const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="down" ref={ref} {...props} />;
  });

  useEffect(() => {
    fetchData();
  }, [page, pageSize]);

  const fetchData = async () => {
    const medsResp = await dispatch(getMedicines({}));
    setMedicines(medsResp?.payload?.data || []);
    await dispatch(getPurchaseOrders({ page, pageSize }));
  };

  /* ---------------------- setters for autocomplete ---------------------- */

  const setPoClinicData = (valueOrUpdater) => {
    const v =
      typeof valueOrUpdater === 'function'
        ? valueOrUpdater({})?.clinicId
        : valueOrUpdater?.clinicId ?? valueOrUpdater?.value ?? valueOrUpdater?._id;
    setPoClinic(v || '');
  };

  const setPoShopData = (valueOrUpdater) => {
    const v =
      typeof valueOrUpdater === 'function'
        ? valueOrUpdater({})?.shopId
        : valueOrUpdater?.shopId ?? valueOrUpdater?.value ?? valueOrUpdater?._id;
    setPoShop(v || '');
  };

  /* ---------------------- PO items helpers ---------------------- */

  const addItem = () => setItems((prev) => [...prev, { medicineId: '', qty: 1 }]);

  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const updateItem = (idx, key, value) => {
    setItems((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [key]: value };
      return copy;
    });
  };

  /* ---------------------- Create PO ---------------------- */

  const handleCreateQuickPo = async () => {
    if (!poClinic) return toast.error('Select clinic');
    if (!poShop) return toast.error('Select pharmacy shop');

    const cleaned = items.filter((i) => i.medicineId && Number(i.qty) > 0);
    if (!cleaned.length) return toast.error('Add at least one medicine with qty');

    const payload = {
      clinicId: poClinic,
      pharmacyShopId: poShop,
      medicines: cleaned.map((it) => {
        const m = medicines.find((x) => x._id === it.medicineId) || {};
        return {
          medicineId: it.medicineId,
          name: m.name || '',
          qty: Number(it.qty),
        };
      }),
    };

    setLoading(true);
    try {
      const resp = await dispatch(createPurchaseOrder(payload));
      if (resp?.payload?.data) {
        setPoOpen(false);
        setPoClinic('');
        setPoShop('');
        setItems([{ medicineId: '', qty: 1 }]);
        toast.success('Purchase Order created');
        fetchData();
      } else {
        toast.error(resp?.payload?.message || 'Failed');
      }
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------- DataGrid columns ---------------------- */

  const openPoReport = (order) => {
    // fetch fresh order details and prepare receive rows
    (async () => {
      setReportLoading(true);
      try {
        const resp = await dispatch(getPurchaseOrderById(order._id));
        const fresh = resp?.payload?.data || order;
        setSelectedOrder(fresh);
        const rows = (fresh.medicines || []).map((m) => ({
          medicineId: m.medicineId || m._id,
          name: m.name,
          orderedQty: m.orderedQty || m.qty || 0,
          receivedQty: m.receivedQty || 0,
          remainingQty: m.remainingQty != null ? m.remainingQty : Math.max(0, (m.orderedQty || m.qty || 0) - (m.receivedQty || 0)),
          receiveNow: 0,
          error: '',
        }));
        setReceiveRows(rows);
        setReportOpen(true);
      } finally {
        setReportLoading(false);
      }
    })();
  };

 const handleDownloadPdf = () => {
  if (!selectedOrder) return;

  const doc = new jsPDF("p", "mm", "a4");
  let y = 15;

  /* ================= HEADER ================= */
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("PURCHASE ORDER INVOICE", 105, y, { align: "center" });

  y += 8;
  doc.setLineWidth(0.5);
  doc.line(10, y, 200, y);
  y += 10;

  /* ================= META ================= */
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);

  doc.text("Invoice No:", 10, y);
  doc.text(`${selectedOrder.poNumber || selectedOrder._id}`, 40, y);

  doc.text("Date:", 140, y);
  doc.text(
    new Date(selectedOrder.createdAt).toLocaleDateString(),
    155,
    y
  );

  y += 7;
  doc.text("Status:", 10, y);
  doc.text(`${selectedOrder.status || "-"}`, 40, y);

  y += 12;

  /* ================= DETAILS ================= */
  doc.setFont("helvetica", "bold");
  doc.text("Clinic Details", 10, y);
  doc.text("Pharmacy Shop Details", 110, y);

  y += 6;
  doc.setFont("helvetica", "normal");

  // Clinic
  doc.text(selectedOrder?.clinicId?.name || "-", 10, y);
  doc.text(selectedOrder?.clinicId?.address || "-", 10, y + 5);

  // Pharmacy
  doc.text(selectedOrder?.pharmacyShopId?.shopName || "-", 110, y);
  doc.text(selectedOrder?.pharmacyShopId?.address || "-", 110, y + 5);
  doc.text(
    `Phone: ${selectedOrder?.pharmacyShopId?.phone || "-"}`,
    110,
    y + 10
  );

  y += 22;

  /* ================= MEDICINE TABLE ================= */
  const tableBody = (selectedOrder?.medicines || []).map((item, index) => [
    index + 1,
    item.name,
    item.orderedQty || item.qty,
  ]);

  autoTable(doc, {
    startY: y,
    head: [["#", "Medicine Name", "Quantity"]],
    body: tableBody,
    theme: "grid",
    styles: {
      fontSize: 10,
      cellPadding: 3,
    },
    headStyles: {
      fillColor: [240, 240, 240],
      textColor: 0,
      fontStyle: "bold",
    },
    columnStyles: {
      0: { cellWidth: 15 },
      1: { cellWidth: 120 },
      2: { cellWidth: 35, halign: "right" },
    },
  });

  /* ================= FOOTER ================= */
  const finalY = doc.lastAutoTable.finalY + 15;
  doc.setFontSize(10);
  doc.setFont("helvetica", "italic");
  doc.text(
    "This is a system generated purchase order invoice.",
    105,
    finalY,
    { align: "center" }
  );

  /* ================= SAVE ================= */
  const filename = `${selectedOrder.poNumber || selectedOrder._id}.pdf`;
  doc.save(filename);
};

  const handleImportRow = async (row) => {
    if (!row) return;
    setReportLoading(true);
    try {
      const resp = await dispatch(importPurchaseOrder(row._id));
      if (resp?.payload?.data) {
        toast.success('Purchase Order imported; stock updated');
        fetchData();
      } else {
        toast.error(resp?.payload?.message || 'Import failed');
      }
    } finally {
      setReportLoading(false);
    }
  };

  const handleCancelRow = async (row) => {
    if (!row) return;
    setReportLoading(true);
    try {
      const resp = await dispatch(cancelPurchaseOrder(row._id));
      if (resp?.payload?.data) {
        toast.success('Purchase Order cancelled');
        fetchData();
      } else {
        toast.error(resp?.payload?.message || 'Cancel failed');
      }
    } finally {
      setReportLoading(false);
    }
  };

  const handleImport = async () => {
    if (!selectedOrder) return;
    await handleImportRow(selectedOrder);
    setReportOpen(false);
  };

  const handleCancel = async () => {
    if (!selectedOrder) return;
    await handleCancelRow(selectedOrder);
    setReportOpen(false);
  };

  const validateReceiveRows = () => {
    if (!receiveRows || !receiveRows.length) return { valid: false, hasValue: false };
    let hasValue = false;
    for (const r of receiveRows) {
      const v = r.receiveNow;
      if (v && Number(v) > 0) hasValue = true;
      if (r.error) return { valid: false, hasValue };
      if (v !== '' && (isNaN(Number(v)) || Number(v) < 0 || Number(v) > Number(r.remainingQty || 0))) return { valid: false, hasValue };
    }
    return { valid: true, hasValue };
  };

  const handleReceiveSubmit = async () => {
    if (!selectedOrder) return;
    const { valid, hasValue } = validateReceiveRows();
    if (!valid || !hasValue) return toast.error('Fix errors or enter quantities to receive');

    const payload = {
      medicines: receiveRows
        .filter((r) => r.receiveNow && Number(r.receiveNow) > 0)
        .map((r) => ({ medicineId: r.medicineId, receivedQty: Number(r.receiveNow) })),
    };

    setReportLoading(true);
    try {
      const resp = await dispatch(receivePurchaseOrder({ id: selectedOrder._id, data: payload }));
      if (resp?.payload?.data) {
        toast.success('Received successfully');
        fetchData();
        setReportOpen(false);
      } else {
        toast.error(resp?.payload?.message || 'Receive failed');
      }
    } finally {
      setReportLoading(false);
    }
  };

  const columns = [
    {
      field: 'actions',
      headerName: 'Action',
      width: 70,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.2, width: '100%' }}>
        <Tooltip title="View P/O">
          <IconButton size="small" color="success" sx={{ p: 0.35 }} onClick={() => openPoReport(params.data)}>
            <ReceiptIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Import / Receive">
          <IconButton
            size="small"
            color="primary"
            sx={{ p: 0.35 }}
            onClick={() => openPoReport(params.data)}
            disabled={params.data?.status === 'IMPORTED' || params.data?.status === 'CANCELLED' || reportLoading}
          >
            <TaskAltIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {params.data?.status === 'CREATED' &&
        <Tooltip title="Cancel">
          <IconButton size="small" color="error" sx={{ p: 0.35 }} onClick={() => handleCancelRow(params.data)} disabled={params.data?.status === 'CANCELLED' || reportLoading}>
            <HighlightOffIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        }
        </Box>
      ),
    },
    { field: 'poNumber', headerName: 'P/O Number', width: 150 },
   {
  field: 'status',
  headerName: 'Status',
  width: 170,
  renderCell: (params) => {
    const status = params?.value || 'N/A';

    const getColor = (status) => {
      switch (status.toLowerCase()) {
        case 'active':
        case 'imported':
          return 'success';
        case 'created':
          return 'primary';
        case 'cancelled':
        case 'error':
          return 'error';
        default:
          return 'default';
      }
    };

    return (
      <Chip
        label={status}
        color={getColor(status)}
        size="small"
        variant="outlined"
        sx={{
          height: 22,
          minWidth: 96,
          fontSize: '0.7rem',
          fontWeight: 700,
          letterSpacing: '0.01em',
          '& .MuiChip-label': { px: 1 },
        }}
      />
    );
  },
},
    {
      field: 'pharmacyShop',
      headerName: 'Pharmacy Shop',
      width: 180,
      renderCell: (params) => params.data?.pharmacyShopId?.shopName || '-',
    },
    {
      field: 'clinic',
      headerName: 'Clinic',
      width: 140,
      renderCell: (params) => params.data?.clinicId?.name || '-',
    },
    {
  field: 'items',
  headerName: 'Items',
  width: 230,
  renderCell: (params) => {
    const medicines = params.data?.medicines || [];

    if (!medicines.length) return 'N/A';

    return medicines
      .map(it => `${it.name} (${it.orderedQty ?? it.qty ?? 0})`)
      .join(', ');
  },
},
{
  field: 'createdAt',
  headerName: 'Created',
  width: 132,
    renderCell: (params) => moment(params.data?.createdAt).format("DD-MM-YYYY HH:mm") || '-',
}


  ];

  return (
    <div style={{ padding: 16 }}>
      {/* Header */}
      <Card className={DoctorStyle.tableCard}>
        <div className={DoctorStyle.tableHeader}>
          <h2 className={DoctorStyle.tableTitle}>Import Medicines</h2>
          <Button
            className={DoctorStyle.addBtn}
            variant="contained"
            size="small"
            startIcon={<AddShoppingCartIcon />}
            onClick={() => {
              setPoClinic('');
              setPoShop('');
              setItems([{ medicineId: '', qty: 1 }]);
              setPoOpen(true);
            }}
          >
            Create P/O
          </Button>
        </div>
      </Card>

      {/* Medicines Grid */}
      <Card sx={{ p: 0.8, border: '1px solid var(--color-border-soft)', boxShadow: '0 4px 14px rgba(21,51,86,0.08)' }}>
        <CommonAgGrid
          rowData={orders}
          columnDefs={columns.map((c) => ({
            ...c,
            field: c.field,
            headerName: typeof c.headerName === 'string' ? c.headerName : c.field,
            cellRenderer: c.renderCell,
            valueGetter: c.valueGetter,
            minWidth: c.width || 140,
            width: c.width,
            flex: c.width ? 0 : 1,
          }))}
          getRowId={(r) => String(r.data?._id)}
          height={280}
          pagination
          loading={ordersLoading}
          paginationMode="server"
          rowCount={Number(totalCount) || 0}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          pageSizeOptions={[5, 10, 20, 50]}
        />
      </Card>

      {/* PO Dialog */}
      <Dialog
        open={poOpen}
        onClose={() => setPoOpen(false)}
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, border: '1px solid var(--color-border-soft)' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: 'var(--color-heading)', borderBottom: '1px solid var(--color-border-soft)' }}>
          Create Purchase Order
        </DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            '& .MuiInputBase-root': { fontSize: '0.84rem' },
            '& .MuiInputLabel-root': { fontSize: '0.8rem' },
          }}
        >
          <SearchClinic
            open={poOpen}
            setData={setPoClinicData}
            data={{ clinicId: poClinic }}
            name="clinicId"
            label="Clinic"
            size="small"
          />

          <SearchPharmacyShop
            open={poOpen}
            setData={setPoShopData}
            data={{ shopId: poShop }}
            name="shopId"
            label="Pharmacy Shop"
            size="small"
          />

          {items.map((row, idx) => (
            <div key={idx} style={{ display: 'flex', gap: 10  }}>
              <SearchMedicine
                open={poOpen}
                value={row.medicineId}
                onChange={(opt) => updateItem(idx, 'medicineId', opt?.value || opt?._id || '')}
                label="Medicine"
                size="small"
                required="true"
              />

              <TextField
                label="Qty"
                type="number"
                size="small"
                value={row.qty}
                onChange={(e) => updateItem(idx, 'qty', e.target.value)}
                sx={{ width: 120 }}
              />

              <Button color="error" onClick={() => removeItem(idx)} disabled={items.length === 1}>
                Remove
              </Button>
            </div>
          ))}

          <Button onClick={addItem} variant="outlined" disabled={!poShop}>
            Add Medicine
          </Button>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid var(--color-border-soft)', p: 1.25 }}>
          <Button onClick={() => setPoOpen(false)} sx={{ textTransform: 'none', borderRadius: '8px', border: '1px solid var(--color-border-soft)', color: 'var(--color-heading)' }}>
            Cancel
          </Button>
          <LoadingButton
            loading={loading}
            variant="contained"
            onClick={handleCreateQuickPo}
            disabled={loading || !poClinic || !poShop || items.filter(i => i.medicineId && Number(i.qty) > 0).length === 0}
            sx={{ textTransform: 'none', borderRadius: '8px', boxShadow: 'none' }}
          >
            Create
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* P/O Report Dialog */}
      <Dialog
      open={reportOpen}
      onClose={() => { setReportOpen(false); setSelectedOrder(null); setReceiveRows([]); }}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 2, border: '1px solid var(--color-border-soft)', overflow: 'hidden' } }}
    >
  <DialogTitle
    sx={{
      py: 1,
      px: 1.5,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: '1px solid var(--color-border-soft)',
      background: '#f8fbff',
    }}
  >
    <Typography variant='subtitle1' fontWeight={700} color="var(--color-heading)">Purchase Order</Typography>
  
<Button
      variant="contained"
      startIcon={<ReceiptIcon />}
      size="small"
      disabled={!selectedOrder}
      onClick={handleDownloadPdf}
      sx={{ textTransform: 'none', borderRadius: '8px', boxShadow: 'none', minHeight: 30, fontSize: '0.75rem' }}
    >
      Download PDF
    </Button>  
  </DialogTitle>

  <DialogContent dividers sx={{ p: 1.25, '& .MuiTypography-root': { fontSize: '0.82rem' } }}>
    {!selectedOrder ? (
      <Typography>No data</Typography>
    ) : (
      <Box sx={{ px: 1 }}>

        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1.5,
          }}
        >
          <Box>
            <Typography fontWeight={600}>P/O No:</Typography>
            <Typography>{selectedOrder.poNumber || selectedOrder._id}</Typography>

           
          </Box>

          <Box textAlign="right">
            <Typography fontWeight={600} mt={1}>Date:</Typography>
            <Typography>
              {moment(selectedOrder.createdAt).format("DD-MM-YYYY")}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 1.25 }} />

        {/* Clinic & Pharmacy */}
        <Grid container spacing={1.25} mb={1.5}>
          <Grid item xs={12} sm={6}>
            <Typography fontWeight={700} sx={{ fontSize: '0.8rem', mb: 0.4 }}>
              Clinic Details
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{selectedOrder?.clinicId?.name}</Typography>
            <Typography sx={{ fontSize: '0.76rem' }} color="text.secondary">
             Address : {selectedOrder?.clinicId?.address || '-'}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography fontWeight={700} sx={{ fontSize: '0.8rem', mb: 0.4 }}>
              Pharmacy Shop Details
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{selectedOrder?.pharmacyShopId?.shopName}</Typography>
            <Typography sx={{ fontSize: '0.76rem' }} color="text.secondary">
             Address : {selectedOrder?.pharmacyShopId?.address || '-'}
            </Typography>
            <Typography sx={{ fontSize: '0.76rem' }}>
              Phone: {selectedOrder?.pharmacyShopId?.phone || '-'}
            </Typography>
          </Grid>
        </Grid>

        {/* Medicines Table */}
        <Typography fontWeight={700} mb={0.8} sx={{ fontSize: '0.82rem' }}>
          Medicine Details
        </Typography>

        <Table
          size="small"
          sx={{
            '& .MuiTableCell-root': { py: 0.55, px: 0.75, fontSize: '0.75rem' },
            '& .MuiFormHelperText-root': { fontSize: '0.66rem', lineHeight: 1.1, mx: 0 },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Medicine Name</TableCell>
              <TableCell align="right">Ordered</TableCell>
              <TableCell align="right">Received</TableCell>
              <TableCell align="right">Remaining</TableCell>
              <TableCell align="right">Receive Now</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {receiveRows.map((item, index) => {
              const liveRemaining = Math.max(0, (item.orderedQty || 0) - ((item.receivedQty || 0) + (Number(item.receiveNow) || 0)));
              return (
                <TableRow key={index}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell align="right">{item.orderedQty}</TableCell>
                  <TableCell align="right">{item.receivedQty}</TableCell>
                  <TableCell align="right">{liveRemaining}</TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={item.receiveNow}
                      disabled={selectedOrder?.status === 'IMPORTED' || selectedOrder?.status === 'CANCELLED'}
                      error={!!item.error}
                      helperText={item.error}
                      onChange={(e) => {
                        const vRaw = e.target.value;
                        const v = vRaw === '' ? '' : Number(vRaw);
                        setReceiveRows((prev) => {
                          const copy = [...prev];
                          copy[index] = { ...copy[index], receiveNow: v };
                          const rem = Number(copy[index].remainingQty || 0);
                          if (v === '') {
                            copy[index].error = '';
                          } else if (isNaN(v) || v < 0) {
                            copy[index].error = 'Invalid qty';
                          } else if (v > rem) {
                            copy[index].error = 'Exceeds remaining';
                          } else {
                            copy[index].error = '';
                          }
                          return copy;
                        });
                      }}
                      sx={{
                        width: 106,
                        '& .MuiInputBase-root': { fontSize: '0.75rem', height: 30 },
                        '& input': { py: 0.45, px: 1 },
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {/* Footer */}
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.7rem' }} color="text.secondary">
            This is a system generated purchase order invoice.
          </Typography>
        </Box>

      </Box>
    )}
  </DialogContent>

  <DialogActions sx={{ borderTop: '1px solid var(--color-border-soft)', py: 0.85, px: 1.25 }}>
    <Button
      onClick={() => { setReportOpen(false); setSelectedOrder(null); setReceiveRows([]); }}
      size="small"
      sx={{ textTransform: 'none', borderRadius: '8px', border: '1px solid var(--color-border-soft)', color: 'var(--color-heading)', minHeight: 30, fontSize: '0.75rem' }}
    >
      Close
    </Button>
    {/* <Button color="error" onClick={handleCancel} disabled={!selectedOrder || selectedOrder?.status === 'CANCELLED' || reportLoading}>
      Cancel
    </Button>
    <Button variant="contained" onClick={handleImport}>
      {reportLoading ? 'Working...' : 'Import'}
    </Button> */}
    <Button
      color="primary"
      variant="contained"
      size="small"
      onClick={handleReceiveSubmit}
      disabled={!selectedOrder || reportLoading || !validateReceiveRows().valid || !validateReceiveRows().hasValue || selectedOrder?.status === 'IMPORTED' || selectedOrder?.status === 'CANCELLED'}
      sx={{ textTransform: 'none', borderRadius: '8px', boxShadow: 'none', minHeight: 30, fontSize: '0.75rem' }}
    >
      {reportLoading ? 'Working...' : 'Receive'}
    </Button>

    
  </DialogActions>
</Dialog>
    </div>
  );
}
